import type { ReactNode, Ref } from "react";

import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { ChevronDownIcon } from "#icons";
import { cn } from "#utils";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";

import { chevronStyles, triggerStyles } from "./selectFieldStyles";

/** SelectFieldProps interface for the SelectField component */
export interface SelectFieldProps extends Omit<
  AriaButtonProps,
  "className" | "children" | "value"
> {
  /** The display value shown in the field */
  value?: ReactNode;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Label text displayed above the field */
  label?: string;
  /** Tooltip text shown next to the label */
  tooltipText?: ReactNode;
  /** Assistive text displayed below the field */
  assistiveText?: ReactNode;
  /** Whether the field is in read-only mode */
  isReadOnly?: boolean;
  /** Whether the field has an invalid value */
  isInvalid?: boolean;
  /** Whether the associated popover/dropdown is open */
  isOpen?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Ref to the button element */
  ref?: Ref<HTMLButtonElement>;
  /** Additional CSS classes for the button */
  buttonClassName?: string;
  /** Optional content rendered before the value (e.g. a leading icon) */
  startAdornment?: ReactNode;
  /** Optional content rendered between the value and chevron (e.g. clear-all button) */
  endAdornment?: ReactNode;
}

/**
 * SelectField component - A trigger element styled like TextField for use with Popovers.
 *
 * This component mimics the appearance of TextField but functions as a button
 * that can trigger dropdowns, popovers, or other selection interfaces.
 */
export function SelectField(props: SelectFieldProps) {
  const {
    value,
    placeholder = "Select an option...",
    label,
    tooltipText,
    assistiveText,
    isDisabled,
    isReadOnly,
    isInvalid = false,
    isOpen = false,
    isRequired,
    className,
    buttonClassName,
    startAdornment,
    endAdornment,
    ref,
    ...restProps
  } = props;
  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <div className={cn("flex w-full flex-col gap-[.15rem]", className)}>
      <FormFieldHeader
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        tooltipText={tooltipText}
      />
      <AriaButton
        {...restProps}
        aria-expanded={isOpen}
        aria-label={props["aria-label"] ?? `select-field ${label ?? ""}`}
        className={triggerStyles({
          isDisabled,
          isReadOnly,
          isInvalid,
          isOpen,
          className: buttonClassName,
        })}
        isDisabled={isDisabled}
        onPress={(e) => {
          if (isReadOnly) {
            return;
          }
          restProps.onPress?.(e);
        }}
        ref={ref}
      >
        {startAdornment ? (
          <span className="mr-xs flex shrink-0 items-center">{startAdornment}</span>
        ) : null}
        <span
          className={cn(
            "flex-1 truncate text-left",
            hasValue ? "text-text-primary" : "text-text-secondary",
            isDisabled ? "text-text-disabled" : "",
          )}
        >
          {hasValue ? value : placeholder}
        </span>
        {endAdornment}
        <ChevronDownIcon
          aria-hidden="true"
          className={chevronStyles({
            isOpen,
            isDisabled,
          })}
        />
      </AriaButton>
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </div>
  );
}
